import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { Squad } from '../../../types';
import { GAME_REGISTRY, GameConfig } from '../../games/models/GameRegistry';

interface SquadGameAccessModalProps {
  squad: Squad | null;
  isOpen: boolean;
  onClose: () => void;
  teacherId?: string;
  teacherName?: string;
}

/**
 * Teacher-facing modal to manage which games are unlocked for a live squad.
 * - Shows all games in a grid with image + title.
 * - Allows toggling individual games and an "Unlock All" shortcut.
 * - Writes updates to squads/{id}.unlockedGames so student collections react in real time.
 */
const SquadGameAccessModal: React.FC<SquadGameAccessModalProps> = ({
  squad,
  isOpen,
  onClose,
  teacherId,
  teacherName,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchedGameId, setLaunchedGameId] = useState<string | null>(null);
  const hasInitRef = useRef(false);

  const isSquadLive = useMemo(() => {
    if (!squad) return false;
    return squad.classStatus === 'current' && !!(squad as any).liveSession;
  }, [squad]);

  const allGames: GameConfig[] = useMemo(
    () => Object.values(GAME_REGISTRY),
    []
  );

  const defaultUnlockedGameIds = useMemo(() => {
    const curriculumKey = String((squad as any)?.curriculumId || '').trim().toLowerCase();
    const isAi2026CreateWorlds = curriculumKey === 'ai-2026-create-worlds';
    if (isAi2026CreateWorlds) {
      return ['real-or-ai', 'og-creators'].filter(id => allGames.some(g => g.id === id));
    }
    return allGames.map(g => g.id);
  }, [allGames, squad]);

  // Initialize local selection from the squad's unlockedGames.
  // Only apply the "undefined = ALL" default the first time the modal opens,
  // and never re-initialize while a save is in-flight (to avoid "flip to ALL").
  useEffect(() => {
    if (!isOpen || !squad || isSaving) return;

    const raw = (squad as any).unlockedGames;
    const hasField = Object.prototype.hasOwnProperty.call(
      squad as any,
      'unlockedGames'
    );

    console.log('[SquadGameAccessModal:initEffect]', {
      squadId: squad.id,
      isOpen,
      isSaving,
      hasField,
      rawUnlockedGames: raw,
      hasInit: hasInitRef.current,
    });

    // 1) Explicit array from Firestore (including empty) → source of truth
    if (Array.isArray(raw)) {
      setSelectedIds(new Set(raw));
      hasInitRef.current = true;
      return;
    }

    // 2) First-time default: field truly missing and we've never initialized this session
    if (!hasInitRef.current && !hasField) {
      setSelectedIds(new Set(defaultUnlockedGameIds));
      hasInitRef.current = true;
      return;
    }

    // 3) Otherwise (undefined / legacy / invalid after first init): do not overwrite local state.
    //    Ignore transient undefined while saves are in-flight to avoid "flip to ALL".
  }, [isOpen, squad, squad?.id, squad?.unlockedGames, allGames, isSaving]);

  if (!isOpen || !squad) return null;

  const persistSelection = async (nextIds: Set<string>) => {
    try {
      setIsSaving(true);
      const squadRef = doc(db, 'squads', squad.id);
      await updateDoc(squadRef, { unlockedGames: Array.from(nextIds) });
    } catch (e) {
      console.error('Failed to update squad unlockedGames', e);
      // Soft-fail: keep local state; teacher can retry
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (gameId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) {
        next.delete(gameId);
      } else {
        next.add(gameId);
      }
      // Fire-and-forget; local state stays consistent even if snapshot flickers.
      void persistSelection(next);
      return next;
    });
  };

  const handleUnlockAll = () => {
    setSelectedIds(() => {
      const allIds = new Set(allGames.map(g => g.id));
      void persistSelection(allIds);
      return allIds;
    });
  };

  const handleLockAll = () => {
    console.log('[SquadGameAccessModal:handleLockAll:click]', {
      squadId: squad?.id,
      beforeSize: selectedIds.size,
    });
    setSelectedIds(prev => {
      const empty = new Set<string>();
      console.log('[SquadGameAccessModal:handleLockAll:setState]', {
        prevSize: prev.size,
        nextSize: empty.size,
      });
      void persistSelection(empty);
      return empty;
    });
  };

  const handleLaunchGame = async (game: GameConfig) => {
    if (!squad || isLaunching || !teacherId || !teacherName) return;

    try {
      setIsLaunching(true);
      setLaunchedGameId(game.id);

      const squadRef = doc(db, 'squads', squad.id);
      const currentVersion = (squad.activeLaunchSession?.version || 0) + 1;
      const sessionId = `${squad.id}-${game.id}-${Date.now()}`;

      await updateDoc(squadRef, {
        activeLaunchSession: {
          gameId: game.id,
          teacherId,
          teacherName,
          sessionId,
          version: currentVersion,
          createdAt: serverTimestamp(),
        },
      });

      setTimeout(() => {
        setLaunchedGameId(null);
      }, 2000);
    } catch (e) {
      console.error('Failed to launch game for class', e);
      void window.appAlert('Failed to launch game. Please try again.');
      setLaunchedGameId(null);
    } finally {
      setIsLaunching(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="attendance-modal-overlay" onClick={handleOverlayClick}>
      <div className="attendance-modal" style={{ display: 'flex', flexDirection: 'column', maxWidth: 960, width: '90%' }}>
        <div className="attendance-modal-header">
          <h2>Manage Games for {squad.title}</h2>
          <button className="close-modal-btn" onClick={onClose}>×</button>
        </div>

        <div className="attendance-modal-content" style={{ overflowY: 'auto', flexGrow: 1 }}>
          {!isSquadLive && (
            <div
              role="alert"
              style={{
                marginBottom: '0.9rem',
                padding: '0.9rem 1rem',
                borderRadius: 12,
                border: '2px solid #ef4444',
                background: '#fef2f2',
                color: '#b91c1c',
                fontWeight: 900,
                fontSize: 16,
                lineHeight: 1.25,
              }}
            >
              This tool only will only work if your class is live. Make sure you start the class first.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#4b5563' }}>
              Select which games this squad can play <strong>right now</strong>. Locked games will still appear for students but show as unavailable.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleUnlockAll}
                style={{
                  padding: '0.4rem 0.9rem',
                  borderRadius: 999,
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                disabled={isSaving}
              >
                Unlock All
              </button>
              <button
                onClick={handleLockAll}
                style={{
                  padding: '0.4rem 0.9rem',
                  borderRadius: 999,
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                disabled={isSaving}
              >
                Lock All
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '0.75rem',
            }}
          >
            {allGames.map(game => {
              const isSelected = selectedIds.has(game.id);
              const isLaunched = launchedGameId === game.id;
              return (
                <div
                  key={game.id}
                  style={{
                    borderRadius: 12,
                    border: isSelected ? '2px solid #10b981' : '1px solid #e5e7eb',
                    padding: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    background: isSelected ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : '#ffffff',
                    boxShadow: isSelected
                      ? '0 4px 10px rgba(16,185,129,0.15)'
                      : '0 2px 6px rgba(15,23,42,0.04)',
                    position: 'relative',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleToggle(game.id)}
                    disabled={isSaving}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        borderRadius: 10,
                        overflow: 'hidden',
                        marginBottom: 6,
                        background: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 80,
                      }}
                    >
                      {game.thumbnail ? (
                        <img
                          src={game.thumbnail}
                          alt={game.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 24 }}>🎮</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={game.title}
                      >
                        {game.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: isSelected ? '#047857' : '#6b7280',
                          padding: '2px 6px',
                          borderRadius: 999,
                          background: isSelected ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.06)',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected ? 'Unlocked' : 'Locked'}
                      </div>
                    </div>
                  </button>
                  {teacherId && teacherName && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLaunchGame(game);
                      }}
                      disabled={isLaunching}
                      style={{
                        marginTop: 6,
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: 'none',
                        background: isLaunched
                          ? 'linear-gradient(135deg, #10b981, #059669)'
                          : 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: '#ffffff',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: isLaunching ? 'not-allowed' : 'pointer',
                        opacity: isLaunching && !isLaunched ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                      }}
                      title="Launch this game for all students in the class"
                    >
                      {isLaunched ? (
                        <>
                          <span>✓</span>
                          <span>Launched!</span>
                        </>
                      ) : (
                        <>
                          <span>Launch for Class</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="attendance-modal-footer" style={{ flexShrink: 0, padding: '0.75rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#6b7280' }}>
          <span>
            {selectedIds.size} of {allGames.length} games unlocked
          </span>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {isLaunching && <span>Launching game for class...</span>}
            {isSaving && <span>Saving changes…</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SquadGameAccessModal;


